import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  financeOrderState,
  fetchFinanceOrderDetail,
  submitFinanceOrder,
  setOrderLineProductsFilter,
  fetchOrderLineProducts,
} from "@redux/financeOrderSlice";
import { Button, Col, Form, Input, message, Modal, Row, Space } from "antd";
import { ColumnsType } from "antd/lib/table";
import financeOrderApi, { contractTypeMap, OrderLineProductsFilter } from "@api/financeOrderApi";
import { StyledSection } from "src/styles/common";
import IntegratedTable from "@component/Table/IntegratedTable";
import DashBoard from "@component/Common/DashBoard";
import ImportFileModal from "@component/ImportFileModal";
import ErrorResultsList from "@component/Common/ErrorResultsList";
import { useParams } from "react-router-dom";

const Header = styled(Row)`
  justify-content: space-between;
  margin: 20px 0;
`;

const Title = styled(Col)`
  font-weight: bold;
  padding-right: 20px;
`;

const Block = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const FinanceOrderDetail = () => {
  const dispatch = useDispatch();
  const { isDetailFetching, financeOrderDetail, financeOrderProductResult, orderLineProductsFilter } =
    useSelector(financeOrderState);
  const { totalAmount, orderLines } = financeOrderDetail;
  const { orderId } = useParams();
  const [showImportPopup, setShowImportPopup] = useState(false);

  const handelImportCSV = async (file: File) => {
    if (!orderId) return;
    try {
      const result = await financeOrderApi.reuploadFinanceOrder(orderId, file);
      message.success(result);
      setShowImportPopup(false);
    } catch (error: any) {
      const { result } = error;
      if (Object.keys(result)?.length === 0) return;

      Modal.warning({
        title: "匯入資料有誤",
        content: <ErrorResultsList errorResult={result} />,
        okText: "我知道了",
        onOk: () => {},
        width: 600,
      });
      setShowImportPopup(false);
    }
  };
  const onFilterSubmit = (values: any) => {
    const payload = {
      ...orderLineProductsFilter,
      ...values,
    };
    dispatch(setOrderLineProductsFilter(payload));
  };
  const onFilterReset = () => {
    const payload = {
      limit: orderLineProductsFilter.limit,
      offset: 0,
    };
    dispatch(setOrderLineProductsFilter(payload));
  };

  const handleSubmit = () => {
    if (!orderId) return;
    dispatch(submitFinanceOrder(orderId));
  };

  const dashBoardData = [
    {
      name: "總數量",
      value: orderLines?.totalQty,
    },
    {
      name: "總月結金額(含稅)",
      value: orderLines?.totalAmount,
      status: orderLines?.totalAmount === totalAmount ? undefined : "error",
    },
  ];

  const columns: ColumnsType<any> = [
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: "SKU(品號)",
      dataIndex: "sku",
    },
    {
      title: "barcode",
      dataIndex: "barcode",
    },
    {
      title: "單品總價",
      dataIndex: "price",
    },
    {
      title: "商品名稱",
      dataIndex: "name",
    },
    {
      title: "數量",
      dataIndex: "qty",
    },
    {
      title: "平台/通路訂單",
      dataIndex: "platformNumber",
    },
  ];

  useEffect(() => {
    if (orderId) {
      const filter = {
        orderId,
        ...orderLineProductsFilter,
      };
      dispatch(fetchOrderLineProducts(filter));
    }
  }, [dispatch, orderId, orderLineProductsFilter]);

  useEffect(() => {
    if (orderId) {
      dispatch(fetchFinanceOrderDetail(orderId));
    }
  }, [dispatch, orderId]);

  return (
    <StyledSection>
      <Header>
        <Col flex="0 1 400px">
          <Space direction="vertical" style={{ display: "flex" }}>
            <Row>
              <Title span={10}>類型</Title>
              <Col>{contractTypeMap[financeOrderDetail.contractType]}</Col>
            </Row>
            <Row>
              <Title span={10}>供應／經銷商</Title>
              <Col>{financeOrderDetail.retailerName}</Col>
            </Row>
            <Block>
              <Title span={10}>訂單總金額(含稅)</Title>
              <Col>{totalAmount}</Col>
            </Block>
          </Space>
        </Col>
        <Col flex="auto">
          <DashBoard dataset={dashBoardData} isFetching={isDetailFetching} />
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              onClick={() => setShowImportPopup(true)}
              disabled={!!financeOrderDetail.submittedAt || !!financeOrderDetail.destroyedAt}
            >
              重新匯入
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!!financeOrderDetail.submittedAt || !!financeOrderDetail.destroyedAt}
            >
              提交
            </Button>
          </Space>
        </Col>
      </Header>
      <Form onFinish={onFilterSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Form.Item label="SKU(品號)" name="sku">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col>
            <Space>
              <Button type="primary" htmlType="submit">
                搜尋
              </Button>
              <Button onClick={onFilterReset}>清除</Button>
            </Space>
          </Col>
        </Row>
      </Form>
      <IntegratedTable
        isPagination
        paginationConfig={{
          totalCount: financeOrderProductResult.count,
          updateFilterParams: (filter: OrderLineProductsFilter) => dispatch(setOrderLineProductsFilter(filter)),
          filterParams: orderLineProductsFilter,
        }}
        loading={isDetailFetching}
        scroll={{ x: "max-content" }}
        columns={columns}
        dataSource={financeOrderProductResult.results}
      />
      {showImportPopup && (
        <ImportFileModal
          csvErrorMessage=""
          closePopup={() => setShowImportPopup(false)}
          clearErrorMessage={() => {}}
          handleUploadFile={handelImportCSV}
          templatePath="admin/template/xls/finance_order_product_template_v1.xlsx"
          fileType=".xlsx"
        />
      )}
    </StyledSection>
  );
};

export default FinanceOrderDetail;
